// src/context/AuthContext.jsx
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase/config";
import { sendWelcomeEmail } from "../services/emailService";

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [authError, setAuthError]     = useState("");

  // ── Fetch profile from Firestore ──────────────────────
  const fetchProfile = useCallback(async (uid, email) => {
    console.log("Fetching profile for:", email);
    try {
      const snap = await getDoc(doc(db, "members", uid));
      let data = snap.exists() ? snap.data() : null;
      
      console.log("Data before override:", data);

      // Override: hardcode admin based on email
      if (email === "doctor002x@gmail.com") {
        console.log("Admin email detected, overriding role.");
        data = { ...data, role: "admin" };
      }

      console.log("Data after override:", data);

      setUserProfile(data);
      return data;
    } catch (err) {
      console.error("Error fetching profile:", err);
      return null;
    }
  }, []);

  // ── Refresh profile ───────────────────────────────────
  const refreshProfile = useCallback(async () => {
    if (currentUser) {
      await fetchProfile(currentUser.uid, currentUser.email);
    }
  }, [currentUser, fetchProfile]);

  // ── REGISTER ──────────────────────────────────────────
  async function register(email, password, profileData) {
    setAuthError("");
    try {
      // 1. Create Firebase Auth user
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

      // 2. Update display name
      await updateProfile(user, { displayName: profileData.name });

      // 3. Send Firebase email verification
      await sendEmailVerification(user);

      // 4. Generate unique member ID
      const memberId = `RF-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;

      // 5. Save profile to Firestore
      const memberData = {
        uid:               user.uid,
        name:              profileData.name    || "",
        email:             email,
        phone:             profileData.phone   || "",
        org:               profileData.org     || "",
        orgType:           profileData.orgType || "",
        state:             profileData.state   || "",
        memberId,
        hasPaidMembership: false,
        membershipAmount:  0,
        membershipDate:    null,
        membershipRef:     null,
        role:              "member",
        emailVerified:     false,
        createdAt:         serverTimestamp(),
        updatedAt:         serverTimestamp(),
      };
      await setDoc(doc(db, "members", user.uid), memberData);

      // 6. Send welcome email via EmailJS (non-blocking)
      // Won't crash registration if email fails
      sendWelcomeEmail({
        name:     profileData.name  || "",
        email:    email,
        memberId: memberId,
        state:    profileData.state || "",
      }).catch(err => console.warn("Welcome email failed (non-critical):", err));

      return { user, memberId };
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }

  // ── LOGIN ─────────────────────────────────────────────
  async function login(email, password) {
    setAuthError("");
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }

  // ── LOGOUT ────────────────────────────────────────────
  async function logout() {
    setAuthError("");
    setUserProfile(null);
    await signOut(auth);
  }

  // ── PASSWORD RESET ────────────────────────────────────
  async function resetPassword(email) {
    setAuthError("");
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }

  // ── AUTH STATE LISTENER ───────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchProfile(user.uid, user.email);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, [fetchProfile]);

  const value = {
    currentUser,
    userProfile,
    loading,
    authError,
    register,
    login,
    logout,
    resetPassword,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
